import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Reveal, Overline } from "./Reveal";
import { PILLARS, IMAGES } from "@/lib/content";

/**
 * PillarSection — a rich, editorial page for a single brand pillar (Welcome/Table/Restore).
 * Props:
 *  - pillarKey: "Welcome" | "Table" | "Restore"
 *  - heroImage: main hero image src
 *  - galleryImages: array of {src, label} for the photo strip
 *  - children: optional extra content (e.g. seasonal menus for Table)
 */
export default function PillarSection({ pillarKey, heroImage, galleryImages = [], children }) {
  const pillar = PILLARS.find((p) => p.tag === pillarKey);
  const [activeIdx, setActiveIdx] = useState(null);
  if (!pillar) return null;

  const accentClass =
    pillar.accent === "oxblood"
      ? "text-oxblood-soft"
      : pillar.accent === "forest"
      ? "text-forest-soft"
      : "text-metallic-gold";

  return (
    <>
      {/* Hero */}
      <section
        className="relative min-h-[70svh] w-full overflow-hidden"
        data-testid={`pillar-hero-${pillarKey.toLowerCase()}`}
      >
        <motion.img
          src={heroImage}
          alt={pillar.title}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 h-full w-full object-cover img-cinematic"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-bg" />
        <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-12 min-h-[70svh] flex flex-col justify-end pb-16 md:pb-24">
          <Reveal>
            <span className={`font-sans text-xs tracking-[0.35em] uppercase ${accentClass}`}>
              {pillar.n} &nbsp;·&nbsp; {pillarKey}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-serif font-light text-ink text-5xl md:text-7xl lg:text-8xl leading-[1] max-w-4xl drop-shadow-[0_2px_20px_rgba(0,0,0,0.9)]">
              {pillar.title}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 font-sans text-lg text-ink/90 max-w-2xl leading-relaxed drop-shadow-[0_2px_10px_rgba(0,0,0,0.9)]">
              {pillar.blurb}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Offerings — single-column tap-to-reveal, same layout on all screens */}
      <section className="relative bg-bg py-24 md:py-32" data-testid={`pillar-offerings-${pillarKey.toLowerCase()}`}>
        <div className="mx-auto max-w-[520px] px-6">
          <div>
            <Reveal>
              <Overline>What we do</Overline>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-serif font-light text-ink text-3xl md:text-4xl lg:text-5xl leading-[1.05]">
                Our <em className={`italic ${accentClass}`}>{pillarKey}</em> offerings
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 font-sans text-sm text-ink/55 max-w-md">
                Tap any offering to see what it looks like.
              </p>
            </Reveal>

            <div className="mt-10 flex flex-col">
              {pillar.items.map((it, i) => {
                const isActive = activeIdx === i;
                return (
                  <Reveal key={it.name} delay={0.04 * i}>
                    <button
                      type="button"
                      onClick={(e) => {
                        const nextIdx = isActive ? null : i;
                        setActiveIdx(nextIdx);
                        if (nextIdx !== null) {
                          requestAnimationFrame(() => {
                            e.currentTarget?.scrollIntoView({
                              behavior: "smooth",
                              block: "start",
                            });
                          });
                        }
                      }}
                      data-testid={`pillar-item-${pillarKey.toLowerCase()}-${i}`}
                      className={`group w-full text-left border-b border-white/10 py-5 md:py-6 flex items-center gap-6 md:gap-8 transition-colors duration-300 ${
                        isActive ? "text-ink" : "text-ink hover:text-ink"
                      }`}
                    >
                      <span className={`font-sans text-xs shrink-0 ${accentClass}`}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span
                        className={`font-serif text-xl md:text-2xl lg:text-3xl leading-tight transition-all duration-300 ${
                          isActive ? "" : "group-hover:translate-x-2"
                        }`}
                      >
                        {it.name}
                      </span>
                      <span
                        className={`ml-auto font-sans text-[0.65rem] tracking-[0.25em] uppercase transition-opacity duration-300 ${
                          isActive ? "text-metallic-gold opacity-100" : "text-ash opacity-60 group-hover:opacity-100"
                        }`}
                      >
                        {isActive ? "Close" : "View"}
                      </span>
                    </button>

                    {/* Inline image reveal — same portrait layout on every screen */}
                    <AnimatePresence initial={false}>
                      {isActive && (
                        <motion.div
                          key="preview"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                          className="overflow-hidden"
                        >
                          <div className="py-6 max-w-[520px]">
                            <div className="relative aspect-[4/5] overflow-hidden border border-white/10">
                              <img
                                src={it.image}
                                alt={it.name}
                                className="h-full w-full object-cover img-cinematic"
                              />
                            </div>
                            {it.blurb && (
                              <p className="mt-4 font-sans text-base text-ink/70 leading-relaxed">
                                {it.blurb}
                              </p>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Photo strip */}
      {galleryImages.length > 0 && (
        <section className="relative bg-surface border-y border-white/10">
          <div className="grid grid-cols-1">
            {galleryImages.map((g, i) => (
              <Reveal key={g.src + i} delay={0.05 * i}>
                <div className="relative aspect-[4/5] overflow-hidden group" data-testid={`pillar-gallery-${pillarKey.toLowerCase()}-${i}`}>
                  <motion.img
                    src={g.src}
                    alt={g.label || pillar.title}
                    className="h-full w-full object-cover img-cinematic"
                    whileHover={{ scale: 1.06 }}
                    transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  {g.label && (
                    <span className={`absolute bottom-6 left-6 font-sans text-[0.65rem] tracking-[0.3em] uppercase ${accentClass}`}>
                      {g.label}
                    </span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Optional extra content (e.g. seasonal menus) */}
      {children}

      {/* CTA */}
      <section className="relative bg-bg py-24 md:py-32">
        <div className="mx-auto max-w-[1600px] px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-serif italic text-2xl md:text-3xl lg:text-4xl text-ink/85 max-w-3xl mx-auto leading-snug">
              {pillar.blurb}
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/book"
              data-testid={`pillar-cta-${pillarKey.toLowerCase()}`}
              className="mt-10 inline-flex items-center gap-4 rounded-full bg-black border border-gold/70 px-8 py-4 font-sans text-xs tracking-[0.2em] uppercase text-metallic-gold hover:border-gold hover:bg-black/80 transition-all duration-300"
            >
              Get in Touch About {pillarKey} →
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
